import classnames from "classnames";
import { useRouter } from "next/router";
import Tooltip from "rc-tooltip";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import {
  BrandDetails as IBrandDetails,
  BrandService as IBrandService,
  BrandServiceName as IBrandServiceName,
} from "shared/types/brands";

import Img from "@components/FileServer/Img";
import {
  FlightTravelPolicies,
  TravelPoliciesBadges,
} from "@components/Flights/TravelPolicies";
import PriceFormatter from "@components/Formatters/PriceFormatter";
import FormattedMessage from "@components/UI/FormattedMessage";
import { CheckIconRoundedBG } from "@components/UI/Icons/Check";
import CrossIcon from "@components/UI/Icons/Cross";
import IconPriceTag from "@components/UI/Icons/PriceTag";

import brandedFaresPickFlight from "@lib/pickFlight";

const BrandServiceSign: React.FC<{ service: IBrandService }> = ({
  service,
}) => {
  switch (service.Included) {
    case "Charged":
      return (
        <div className="table-cell brands-table-service-symbol -charged">
          <Tooltip
            placement="topRight"
            overlay={
              <span>
                <FormattedMessage id="BrandedFares.ModalTable.Symbols.Charged" />
              </span>
            }
            overlayClassName="brands-table-service-symbol-tooltip"
          >
            <div>
              <IconPriceTag size="md" />
            </div>
          </Tooltip>
        </div>
      );
    case "Mixed":
      return <span style={{ color: "#808080" }}> </span>;
    case "Unknown":
      return <span style={{ color: "#808080" }}> </span>;
    case "No":
      return (
        <div className="table-cell brands-table-service-symbol -not-included">
          <Tooltip
            placement="topRight"
            overlay={
              <span>
                <FormattedMessage id="BrandedFares.ModalTable.Symbols.NotIncluded" />
              </span>
            }
            overlayClassName="brands-table-service-symbol-tooltip"
          >
            <div>
              <CrossIcon size="sm" color="dark-gray" />
            </div>
          </Tooltip>
        </div>
      );
    case "Yes":
      return (
        <div className="table-cell brands-table-service-symbol -included">
          <Tooltip
            placement="topRight"
            overlay={
              <span>
                <FormattedMessage id="BrandedFares.ModalTable.Symbols.Included" />
              </span>
            }
            overlayClassName="brands-table-service-symbol-tooltip"
          >
            <div>
              <CheckIconRoundedBG
                color="white"
                backgroundColor="yellow"
                inlineSize={{ width: "11.49px", height: "8.76px" }}
                backgroundSize="md"
                backgroundType="rounded"
              />
            </div>
          </Tooltip>
        </div>
      );
    default:
      return <span> </span>;
  }
};

const BrandServiceName: React.FC<{
  filteredServices: string[];
  brandName: IBrandServiceName & { tooltipText: string };
}> = ({ filteredServices, brandName }) => {
  if (filteredServices?.find((service) => service === brandName.Type)) {
    return null;
  }

  const isValidTooltipText =
    brandName.tooltipText &&
    brandName.tooltipText.length > 0 &&
    !brandName.tooltipText.startsWith("BrandedFares.");

  return (
    <div
      className={`table-cell brands-table-service-name-center -${brandName.Type}`}
    >
      {isValidTooltipText ? (
        <Tooltip
          placement="topRight"
          overlay={<span>{brandName.tooltipText}</span>}
          overlayClassName="brands-table-service-name-tooltip"
        >
          <span className="brands-table-service-name-text-with-tooltip">
            {brandName.$t || brandName.Type}
          </span>
        </Tooltip>
      ) : (
        <span className="brands-table-service-name-text">
          {brandName.$t || brandName.Type}
        </span>
      )}
    </div>
  );
};

const BrandsTable: React.FC<{
  brandsData: IBrandDetails[];
  brandServiceNames: (IBrandServiceName & { tooltipText: string })[];
  currency: string;
  filteredServices: string[];
  brandsPolicies: any;
  isCbt?: boolean;
}> = ({
  brandsData,
  brandServiceNames,
  currency,
  filteredServices,
  brandsPolicies,
  isCbt,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const [popupOpened, setPopupOpened] = useState<boolean>(false);
  const [policyResultForPopup, setPolicyResultForPopup] = useState<any>(null);

  const serviceTypes = [
    ...new Set(
      brandsData.flatMap((flight) =>
        flight.BrandServices.BrandService.map((service) => service.Type)
      )
    ),
  ];

  const serviceData = {};
  serviceTypes.forEach((type) => {
    serviceData[type] = brandsData.map((flight) => ({
      brand: flight.UniformBrandName,
      included:
        flight.BrandServices.BrandService.find(
          (service) => service.Type === type
        )?.Included || "Unknown",
    }));
  });

  const headers = [
    "Options",
    ...brandsData.map((flight) => flight.UniformBrandName),
  ];

  const rows = serviceTypes.map((type) => [
    type,
    ...serviceData[type].map((data) => data.included),
  ]);

  const filteredRows = rows.filter((row) => {
    return row
      .slice(1)
      .some((cell) => cell !== "No" && cell !== "Unknown" && cell !== "Mixed");
  });

  const buttons = [];
  const policyResults = [];
  const passedPolicy = [];
  const brandCheckedBaggageService = [];
  const travelPolicies = {};
  const price = [];
  const travelPolicyBadges = [];

  brandsData.forEach((brand) => {
    const policyResult = brandsPolicies?.TravelPoliciesResults.find(
      (policy) =>
        !!policy.Value.find((pv) =>
          pv.Key.includes(brand.flightCombination.PricingDetailKey)
        )
    );
    if (policyResult) {
      policyResults.push(policyResult);
    }

    brandCheckedBaggageService.push(
      brand.BrandServices.BrandService.find(
        (service) => service.Type === "CheckedBaggage"
      ).Included
    );

    price.push(
      brand.PricingDetail.FlightPricing.FlightPrice.DisplayPricePerPassenger
    );
  });

  brandsData.forEach((_, index) =>
    passedPolicy.push(policyResults[index]?.Value.every((pv) => !!pv.Result))
  );

  (brandsPolicies?.TravelPolicies ?? []).forEach((policyItem) => {
    travelPolicies[policyItem.Id] = policyItem;
  });

  brandsData.forEach((brand, index) =>
    buttons.push(
      <button
        type="button"
        key={index}
        className="brands-table-select-btn button-secondary-color"
        onClick={() =>
          brandedFaresPickFlight(
            router,
            brand.flightCombination,
            brand.flightCombination.References.Reference.map((ref) => ref.$t),
            policyResults[index],
            brandCheckedBaggageService[index],
            undefined,
            true
          )
        }
      >
        <FormattedMessage id="BrandedFares.ModalTable.SelectBrand" />
      </button>
    )
  );

  policyResults.forEach((result) => {
    const passedPolicyForColumn = result?.Value.every((pv) => !!pv.Result);

    if (!!isCbt && !!result?.Value) {
      travelPolicyBadges.push(
        <>
          <div style={{ height: 19 }}>
            <TravelPoliciesBadges travellers={result?.Value} />
          </div>
          <div
            role="button"
            className={classnames("brands-table-travel-policy", {
              "brands-table-travel-policy--passed": passedPolicyForColumn,
            })}
            onClick={async () => {
              await setPolicyResultForPopup(result);
              await setPopupOpened(true);
            }}
          >
            {passedPolicyForColumn ? (
              <Img
                src="/static/images/round-checkmark.svg"
                className="travel-policy-icon brands-table-travel-policy-icon travel-policy-passed"
                alt={intl.formatMessage({
                  id: "TravelPolicy.policiesPassedTitle",
                })}
                title={intl.formatMessage({
                  id: "TravelPolicy.policiesPassedTitle",
                })}
              />
            ) : (
              <Img
                src="/static/images/cross-gray.svg"
                className="travel-policy-icon brands-table-travel-policy-icon travel-policy-failed"
                alt={intl.formatMessage({ id: "TravelPolicy.policyDenied" })}
                title={intl.formatMessage({
                  id: "TravelPolicy.policyDenied",
                })}
              />
            )}
            <FormattedMessage id="FlightOffer.travelPolicy" />
          </div>
        </>
      );
    } else {
      travelPolicyBadges.push(null);
    }
  });

  return (
    <table>
      <thead className="table-header-row">
        <tr>
          {headers.map((header, index) =>
            index === 0 ? (
              <th className="table-cell table-header-cell-new-first">
                <div className="table-header-text">
                  <FormattedMessage id="BrandedFares.ModalTable.Options" />
                </div>
              </th>
            ) : (
              <th className="table-cell table-header-cell-new" key={index}>
                <div className="table-header-text">{header}</div>
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody>
        {filteredRows.map((row, index) => (
          <tr className="brands-table-row" key={index}>
            {row.map((cell, cellIndex) => (
              <td
                className={
                  cellIndex !== 0
                    ? `table-cell brands-table-service-name -${row[0]}`
                    : ``
                }
                key={cellIndex}
              >
                {cellIndex === 0 ? (
                  <BrandServiceName
                    brandName={brandServiceNames.find(
                      (brandService) => brandService.Type === cell
                    )}
                    filteredServices={filteredServices}
                  />
                ) : (
                  cell !== "Unknown" && (
                    <BrandServiceSign
                      service={{ Type: row[0], Included: cell }}
                    />
                  )
                )}
              </td>
            ))}
          </tr>
        ))}
        <tr>
          {[null, ...travelPolicyBadges].map((travelPolicyBadge, index) => (
            <td className="table-footer-cell-news" key={index}>
              <div className="table-footer-center-cell">
                {travelPolicyBadge}
              </div>
            </td>
          ))}
        </tr>
        <tr>
          {[null, ...price].map((_price, index) => (
            <td key={index} className="brands-service-price">
              {_price !== null ? (
                <PriceFormatter price={_price} customCurrency={currency} />
              ) : null}
            </td>
          ))}
        </tr>
        <tr>
          {[null, ...buttons].map((button, index) => (
            <td className="table-footer-cell-new" key={index}>
              <div className="table-footer-center-cell">{button}</div>
            </td>
          ))}
        </tr>
      </tbody>
      {policyResultForPopup && (
        <FlightTravelPolicies
          isOpened={popupOpened}
          onClose={() => setPopupOpened(false)}
          travellers={policyResultForPopup?.Value}
          travelPolicies={travelPolicies}
        />
      )}
    </table>
  );
};

export default BrandsTable;
